(() => {
  const ROOT_SELECTOR = '[data-woodarq-pdp-hero]';
  const OWN_HIDDEN_ATTR = 'data-woodarq-glo-hidden';
  const PRESERVED_ATTR = 'data-woodarq-gallery-preserved';

  const normalize = value => String(value ?? '').trim().replace(/\s+/g, ' ');
  const normalizeKey = value => normalize(value).toLowerCase();

  const parseJson = (root, selector, fallback = []) => {
    const node = root.querySelector(selector);
    if (!node) return fallback;

    try {
      return JSON.parse(node.textContent || '');
    } catch (error) {
      console.warn('Woodarq PDP GLO bridge: could not parse JSON', error);
      return fallback;
    }
  };

  const getMediaId = variant => {
    return String(
      variant?.featured_media?.id ||
      variant?.featured_image?.id ||
      variant?.image_id ||
      variant?.featured_media_id ||
      ''
    ).trim();
  };

  const getSlides = root => Array.from(root.querySelectorAll('.wd-pdp__slide[data-wd-media-id], .product__media-item[data-wd-media-id]'));
  const getDots = root => Array.from(root.querySelectorAll('.wd-pdp__dot[data-wd-media-id], .thumbnail-list__item[data-wd-media-id]'));

  const externalHiddenSelector = [
    '[data-variant-wizard-hidden="true"]',
    '[data-productwiz-hidden="true"]',
    '[data-pw-hidden="true"]',
    '[data-globo-hidden="true"]',
    '[data-glo-hidden="true"]',
    '.variant-wizard-hidden',
    '.productwiz-hidden',
    '.pw-hidden',
    '.globo-hidden',
    '.glo-hidden'
  ].join(', ');

  const isExternallyHidden = element => {
    if (!element) return true;
    if (element.matches?.(externalHiddenSelector)) return true;

    const inlineDisplay = element.style?.getPropertyValue('display');
    const inlineVisibility = element.style?.getPropertyValue('visibility');
    const inlineOpacity = element.style?.getPropertyValue('opacity');
    if (inlineDisplay === 'none' || inlineVisibility === 'hidden' || inlineOpacity === '0') return true;

    // Native/bridge filters intentionally hide media with theme CSS. Ignore
    // that computed display value while checking whether GLO has produced its
    // own result, but still honour explicit app classes and inline styles above.
    if (element.hasAttribute(OWN_HIDDEN_ATTR) || element.hasAttribute('data-woodarq-native-hidden')) return false;
    if (element.hidden) return true;

    const mediaTargets = [
      element.querySelector?.('.wd-pdp__media-button'),
      element.querySelector?.('.wd-pdp__media-button img'),
      element.querySelector?.('.thumbnail'),
      element.querySelector?.('.thumbnail img')
    ].filter(Boolean);

    if (mediaTargets.some(target => {
      if (target.hasAttribute?.(OWN_HIDDEN_ATTR)) return false;
      if (target.hidden) return true;
      if (target.matches?.(externalHiddenSelector)) return true;
      const targetDisplay = target.style?.getPropertyValue('display');
      const targetVisibility = target.style?.getPropertyValue('visibility');
      const targetOpacity = target.style?.getPropertyValue('opacity');
      return targetDisplay === 'none' || targetVisibility === 'hidden' || targetOpacity === '0';
    })) {
      return true;
    }

    const style = window.getComputedStyle ? window.getComputedStyle(element) : null;
    return Boolean(style && (style.display === 'none' || style.visibility === 'hidden'));
  };

  const isVisibleAfterOwnFilter = element => {
    if (!element || element.hasAttribute(OWN_HIDDEN_ATTR) || element.hasAttribute('data-woodarq-native-hidden')) return false;
    return !isExternallyHidden(element);
  };

  const clearPreservedMedia = root => {
    [...getSlides(root), ...getDots(root)].forEach(element => element.removeAttribute(PRESERVED_ATTR));
  };

  const lockGalleryToPreservedMedia = root => {
    [...getSlides(root), ...getDots(root)].forEach(element => {
      element.toggleAttribute(OWN_HIDDEN_ATTR, !element.hasAttribute(PRESERVED_ATTR));
    });
  };

  const preserveMediaIds = (root, mediaIds) => {
    const allowed = new Set((Array.isArray(mediaIds) ? mediaIds : []).map(String).filter(Boolean));
    clearPreservedMedia(root);

    [...getSlides(root), ...getDots(root)].forEach(element => {
      const mediaId = String(element.dataset.wdMediaId || '').trim();
      element.toggleAttribute(PRESERVED_ATTR, allowed.has(mediaId));
    });
  };

  const preserveCurrentGallery = root => {
    const visibleSlides = getSlides(root).filter(isVisibleAfterOwnFilter);
    const activeSlide = getSlides(root).find(slide => slide.classList.contains('is-active'));
    const source = visibleSlides.length ? visibleSlides : (activeSlide ? [activeSlide] : []);
    preserveMediaIds(root, source.map(slide => slide.dataset.wdMediaId));
  };

  const clearThemeFilters = root => {
    [...getSlides(root), ...getDots(root)].forEach(element => {
      element.removeAttribute(OWN_HIDDEN_ATTR);
      element.removeAttribute('data-woodarq-native-hidden');
      if (element.getAttribute('aria-hidden') === 'true' && element.classList.contains('is-active')) {
        element.setAttribute('aria-hidden', 'false');
      }
    });
  };

  const updateMosaicFromVisibility = root => {
    const slides = getSlides(root);
    const visibleSlides = slides.filter(isVisibleAfterOwnFilter);
    const visibleSet = new Set((visibleSlides.length ? visibleSlides : slides).map(slide => slide));

    slides.forEach(slide => {
      slide.classList.toggle('is-mosaic-visible', visibleSet.has(slide));
      slide.classList.remove('is-mosaic-forced');
    });
  };

  const buildVariantMediaGroups = (root, variants) => {
    const slides = getSlides(root);
    const slideMediaIds = slides.map(slide => String(slide.dataset.wdMediaId || '').trim()).filter(Boolean);
    if (slideMediaIds.length < 2 || !Array.isArray(variants) || !variants.length) return new Map();

    const slideIndexById = new Map(slideMediaIds.map((id, index) => [id, index]));
    const featuredStartsByMediaId = new Map();

    variants.forEach(variant => {
      const mediaId = getMediaId(variant);
      if (!mediaId || !slideIndexById.has(mediaId) || featuredStartsByMediaId.has(mediaId)) return;
      featuredStartsByMediaId.set(mediaId, slideIndexById.get(mediaId));
    });

    const starts = Array.from(featuredStartsByMediaId.entries())
      .map(([mediaId, index]) => ({ mediaId, index }))
      .sort((a, b) => a.index - b.index);

    if (!starts.length) return new Map();

    const groupByFeaturedMediaId = new Map();
    starts.forEach((start, index) => {
      const nextStart = starts[index + 1]?.index ?? slideMediaIds.length;
      const group = slideMediaIds.slice(start.index, Math.max(start.index + 1, nextStart));
      if (group.length) groupByFeaturedMediaId.set(start.mediaId, group);
    });

    const groupByVariantId = new Map();
    variants.forEach(variant => {
      const mediaId = getMediaId(variant);
      const group = groupByFeaturedMediaId.get(mediaId);
      if (group?.length) groupByVariantId.set(String(variant.id), group);
    });

    return groupByVariantId;
  };

  const selectedOptions = root => {
    const indexes = new Set();
    root.querySelectorAll('[data-option-button]').forEach(button => indexes.add(Number(button.dataset.optionIndex || 0)));
    root.querySelectorAll('[data-variant-bridge-select]').forEach(select => indexes.add(Number(select.dataset.optionIndex || 0)));

    return Array.from(indexes).sort((a, b) => a - b).map(index => {
      const active = root.querySelector(`[data-option-button][data-option-index="${index}"].is-active`);
      if (active) return active.dataset.value || '';

      const select = root.querySelector(`[data-variant-bridge-select][data-option-index="${index}"]`);
      return select?.value || '';
    });
  };

  const findVariantByOptions = (variants, options) => {
    if (!Array.isArray(variants) || !Array.isArray(options)) return null;
    return variants.find(variant => {
      return Array.isArray(variant.options) && variant.options.every((option, index) => normalize(option) === normalize(options[index]));
    }) || null;
  };

  const syncNativeBridge = (root, options, variant) => {
    const variantBridge = root.querySelector('[data-woodarq-variant-bridge]');
    const controls = [];

    root.querySelectorAll('[data-variant-bridge-select]').forEach(select => {
      const optionIndex = Number(select.dataset.optionIndex || 0);
      const nextValue = options[optionIndex];
      if (typeof nextValue === 'undefined') return;
      if (select.value !== nextValue) {
        select.value = nextValue;
      }
      controls.push(select);
    });

    const idInputs = root.querySelectorAll('[data-variant-id-input], form[action*="/cart/add"] input[name="id"], form[action*="/cart/add.js"] input[name="id"]');
    idInputs.forEach(input => {
      if (!variant?.id) return;
      input.value = variant.id;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Always publish the native select events. The custom PDP has normally
    // updated the select value already, but GLO still needs the events in order
    // to run its assigned-image logic.
    controls.forEach(control => {
      control.dispatchEvent(new Event('input', { bubbles: true }));
      control.dispatchEvent(new Event('change', { bubbles: true }));
    });

    if (variantBridge) {
      variantBridge.dispatchEvent(new Event('input', { bubbles: true }));
      variantBridge.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  const syncCustomButtonsFromOptions = (root, options) => {
    if (!Array.isArray(options)) return;

    options.forEach((value, index) => {
      root.querySelectorAll(`[data-option-button][data-option-index="${index}"]`).forEach(button => {
        button.classList.toggle('is-active', normalize(button.dataset.value) === normalize(value));
      });

      const label = root.querySelector(`[data-option-label="${index}"]`);
      if (label && value) label.textContent = value;
    });
  };

  const elementFingerprint = element => {
    if (!element) return '';
    return `${element.id || ''} ${element.className || ''} ${Array.from(element.getAttributeNames?.() || []).join(' ')}`.toLowerCase();
  };

  const isGloboElement = element => {
    if (!element) return false;
    const fingerprint = elementFingerprint(element);
    if (/\bglobo\b|\bglo[-_]|globoswatch|g-?swatch/.test(fingerprint)) return true;
    const ancestor = element.closest?.('[class*="globo"], [id*="globo"], [class*="glo-"], [id*="glo-"], [class*="glo_"], [id*="glo_"], [class*="swatch"], [id*="swatch"]');
    return Boolean(ancestor && !ancestor.closest?.(ROOT_SELECTOR)?.querySelector?.('[data-option-button]')?.contains?.(element));
  };

  const hasExactControlValue = (element, value) => {
    const target = normalizeKey(value);
    if (!target) return false;

    const candidates = [
      element.getAttribute?.('data-value'),
      element.getAttribute?.('data-option-value'),
      element.getAttribute?.('data-option'),
      element.getAttribute?.('value'),
      element.getAttribute?.('title'),
      element.getAttribute?.('aria-label'),
      element.dataset?.value,
      element.dataset?.optionValue,
      element.textContent
    ];

    return candidates.some(candidate => normalizeKey(candidate) === target);
  };

  const syncGloboControls = (root, options) => {
    if (!Array.isArray(options) || !options.length) return;

    options.forEach(value => {
      if (!value) return;

      const candidates = Array.from(document.querySelectorAll('input, button, label, [role="button"], [data-value], [data-option-value], [title], [aria-label]'))
        .filter(element => {
          if (element.closest?.(`${ROOT_SELECTOR} [data-option-button]`)) return false;
          if (element.closest?.(`${ROOT_SELECTOR} [data-woodarq-variant-bridge]`)) return false;
          if (!isGloboElement(element)) return false;
          if (!hasExactControlValue(element, value)) return false;
          return true;
        });

      const control = candidates.find(element => element.matches?.('input, button, label, [role="button"]')) || candidates[0];
      if (!control) return;

      try {
        if (control.matches?.('input[type="radio"], input[type="checkbox"]')) {
          if (!control.checked) {
            control.click?.();
          } else {
            control.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else {
          control.click?.();
        }
      } catch (error) {
        // The theme-level fallback below still runs if the app ignores synthetic clicks.
      }
    });
  };

  const hasExternalAppFiltered = root => {
    const slides = getSlides(root);
    if (slides.length < 2) return false;
    const externallyHiddenCount = slides.filter(isExternallyHidden).length;
    return externallyHiddenCount > 0 && externallyHiddenCount < slides.length;
  };

  const setActiveMedia = (root, mediaId) => {
    const slides = getSlides(root);
    const dots = getDots(root);
    const targetMediaId = String(mediaId || '').trim();
    if (!targetMediaId) return;

    slides.forEach(slide => {
      const isTarget = String(slide.dataset.wdMediaId || '').trim() === targetMediaId;
      slide.classList.toggle('is-active', isTarget);
      if (!slide.hasAttribute(OWN_HIDDEN_ATTR)) {
        slide.setAttribute('aria-hidden', isTarget ? 'false' : 'true');
      }
    });

    dots.forEach(dot => {
      const isTarget = String(dot.dataset.wdMediaId || '').trim() === targetMediaId;
      dot.classList.toggle('is-active', isTarget);
      dot.toggleAttribute('aria-current', isTarget);
      const button = dot.querySelector('button');
      if (button) button.toggleAttribute('aria-current', isTarget);
    });

    const sectionId = root.dataset.sectionId;
    const mediaGallery = root.querySelector('media-gallery');
    if (sectionId && mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
      try {
        mediaGallery.setActiveMedia(`${sectionId}-${targetMediaId}`, false);
      } catch (error) {
        // Custom gallery state above is enough if a third-party media-gallery throws.
      }
    }
  };

  const applyFallbackGroup = (root, variantMediaGroups, variant) => {
    const slides = getSlides(root);
    const dots = getDots(root);
    if (!slides.length || !variant?.id) return [];

    const group = variantMediaGroups.get(String(variant.id));
    if (!group?.length || group.length >= slides.length) {
      root.__woodarqPdpGallery?.clearMediaFilter?.('auto');
      updateMosaicFromVisibility(root);
      return slides.map(slide => String(slide.dataset.wdMediaId || '').trim()).filter(Boolean);
    }

    const featuredMediaId = getMediaId(variant);
    const activeMediaId = group.map(String).includes(featuredMediaId) ? featuredMediaId : group[0];

    if (root.__woodarqPdpGallery?.setAllowedMediaIds) {
      root.__woodarqPdpGallery.setAllowedMediaIds(group, activeMediaId, 'auto');
    } else {
      const allowed = new Set(group.map(String));
      slides.forEach(slide => {
        const mediaId = String(slide.dataset.wdMediaId || '').trim();
        const visible = allowed.has(mediaId);
        slide.toggleAttribute(OWN_HIDDEN_ATTR, !visible);
        if (!visible) slide.setAttribute('aria-hidden', 'true');
      });

      dots.forEach(dot => {
        const mediaId = String(dot.dataset.wdMediaId || '').trim();
        dot.toggleAttribute(OWN_HIDDEN_ATTR, !allowed.has(mediaId));
      });

      setActiveMedia(root, activeMediaId);
      updateMosaicFromVisibility(root);
    }

    return group.map(String);
  };

  const scheduleGallerySync = (root, variantMediaGroups, variant, options = {}) => {
    if (!variant) return;
    const selected = options.selectedOptions || selectedOptions(root);
    const selectionKey = `${variant.id}:${selected.map(normalizeKey).join('|')}`;
    const previousState = root.__woodarqGloGallerySyncState;

    // A single Woodarq selection is published through the button, the bridge,
    // and the custom variant event. Treat those as one transaction so GLO is
    // not asked to rebuild the same gallery several times in succession.
    if (previousState?.selectionKey === selectionKey) return;

    previousState?.timers?.forEach(timer => window.clearTimeout(timer));
    preserveCurrentGallery(root);
    lockGalleryToPreservedMedia(root);

    const state = {
      selectionKey,
      timers: [],
      lastSignature: '',
      signatureSince: 0,
      fallbackApplied: false,
      settledByGlo: false
    };
    root.__woodarqGloGallerySyncState = state;

    const isCurrent = () => root.__woodarqGloGallerySyncState === state;
    const addTimer = (callback, delay) => {
      const timer = window.setTimeout(() => {
        state.timers = state.timers.filter(item => item !== timer);
        if (isCurrent()) callback();
      }, delay);
      state.timers.push(timer);
      return timer;
    };

    const commitGloResult = visibleSlides => {
      if (!isCurrent() || state.settledByGlo) return;
      state.settledByGlo = true;
      state.timers.forEach(timer => window.clearTimeout(timer));
      state.timers = [];

      // Both changes happen in the same task, so the browser paints either the
      // preserved old set or the complete GLO set—never GLO's empty midpoint.
      clearThemeFilters(root);
      clearPreservedMedia(root);
      updateMosaicFromVisibility(root);

      const featuredMediaId = getMediaId(variant);
      const targetSlide = visibleSlides.find(slide => String(slide.dataset.wdMediaId || '') === featuredMediaId) || visibleSlides[0];
      if (targetSlide?.dataset?.wdMediaId) setActiveMedia(root, targetSlide.dataset.wdMediaId);
    };

    const inspectGloResult = () => {
      if (!isCurrent() || state.settledByGlo) return false;

      const slides = getSlides(root);
      const visibleSlides = slides.filter(slide => !isExternallyHidden(slide));
      const hiddenCount = slides.length - visibleSlides.length;
      const featuredMediaId = getMediaId(variant);
      const includesFeatured = !featuredMediaId || visibleSlides.some(slide => String(slide.dataset.wdMediaId || '') === featuredMediaId);
      const hasUsableResult = visibleSlides.length > 0 && hiddenCount > 0 && includesFeatured;

      if (!hasUsableResult) {
        state.lastSignature = '';
        state.signatureSince = 0;
        return false;
      }

      const signature = visibleSlides.map(slide => String(slide.dataset.wdMediaId || '')).join('|');
      const now = Date.now();
      if (signature !== state.lastSignature) {
        state.lastSignature = signature;
        state.signatureSince = now;
        addTimer(inspectGloResult, 70);
        return false;
      }

      if (now - state.signatureSince < 55) {
        addTimer(inspectGloResult, 55 - (now - state.signatureSince));
        return false;
      }

      commitGloResult(visibleSlides);
      return true;
    };
    state.inspectGloResult = inspectGloResult;

    const applyProvisionalFallback = () => {
      if (!isCurrent() || state.settledByGlo || state.fallbackApplied) return;
      state.fallbackApplied = true;
      const fallbackIds = applyFallbackGroup(root, variantMediaGroups, variant);
      preserveMediaIds(root, fallbackIds);
      lockGalleryToPreservedMedia(root);
    };

    root.__woodarqGloBridgeSyncing = true;
    try {
      syncNativeBridge(root, selected, variant);
    } finally {
      window.setTimeout(() => { root.__woodarqGloBridgeSyncing = false; }, 0);
    }
    syncGloboControls(root, selected);

    [0, 90, 180, 360, 700, 1200, 1750].forEach(delay => addTimer(inspectGloResult, delay));
    addTimer(applyProvisionalFallback, 360);
  };

  const initRoot = root => {
    if (!root || root.__woodarqGloVariantGalleryReady) return;
    root.__woodarqGloVariantGalleryReady = true;

    const variants = parseJson(root, '[data-variant-json]', []);
    const variantMediaGroups = buildVariantMediaGroups(root, variants);

    const syncFromCurrentSelection = source => {
      const options = selectedOptions(root);
      const variant = findVariantByOptions(variants, options);
      if (!variant) return;
      scheduleGallerySync(root, variantMediaGroups, variant, { selectedOptions: options, source });
    };

    root.addEventListener('click', event => {
      if (!event.target?.closest?.('[data-option-button]')) return;
      window.setTimeout(() => syncFromCurrentSelection('woodarq-option-click'), 0);
    });

    root.querySelector('[data-woodarq-variant-bridge]')?.addEventListener('change', () => {
      if (root.__woodarqGloBridgeSyncing) return;
      const options = selectedOptions(root);
      const bridgeOptions = Array.from(root.querySelectorAll('[data-variant-bridge-select]'))
        .sort((a, b) => Number(a.dataset.optionIndex || 0) - Number(b.dataset.optionIndex || 0))
        .map(select => select.value);
      const nextOptions = bridgeOptions.length ? bridgeOptions : options;
      const variant = findVariantByOptions(variants, nextOptions);
      if (!variant) return;

      syncCustomButtonsFromOptions(root, nextOptions);
      scheduleGallerySync(root, variantMediaGroups, variant, { selectedOptions: nextOptions, source: 'native-bridge-change' });
    });

    root.addEventListener('woodarq:pdp:variant-change', event => {
      const variant = event.detail?.variant;
      const options = event.detail?.selectedOptions || selectedOptions(root);
      if (!variant) return;
      scheduleGallerySync(root, variantMediaGroups, variant, { selectedOptions: options, source: 'woodarq-event' });
    });

    if (window.MutationObserver) {
      let refreshTimer = null;
      const observer = new MutationObserver(mutations => {
        if (root.__woodarqGloBridgeSyncing) return;
        const appMutation = mutations.some(mutation => {
          const target = mutation.target;
          if (!target?.closest?.(ROOT_SELECTOR)) return false;

          if (mutation.attributeName === 'class') {
            const previousClass = String(mutation.oldValue || '');
            const hadExternalHiddenClass = /(?:^|\s)(?:variant-wizard-hidden|productwiz-hidden|pw-hidden|globo-hidden|glo-hidden)(?:\s|$)/.test(previousClass);
            return hadExternalHiddenClass || target.matches?.(externalHiddenSelector);
          }

          return mutation.attributeName !== OWN_HIDDEN_ATTR && mutation.attributeName !== 'data-woodarq-native-hidden';
        });
        if (!appMutation) return;

        window.clearTimeout(refreshTimer);
        refreshTimer = window.setTimeout(() => {
          const state = root.__woodarqGloGallerySyncState;
          if (state && !state.settledByGlo) {
            // Let the active transaction verify that GLO's visible set is
            // complete and stable before releasing the preserved gallery.
            state.inspectGloResult?.();
            return;
          }

          if (!hasExternalAppFiltered(root)) return;
          clearThemeFilters(root);
          clearPreservedMedia(root);
          updateMosaicFromVisibility(root);
        }, 40);
      });

      [...getSlides(root), ...getDots(root)].forEach(element => {
        observer.observe(element, {
          attributes: true,
          attributeOldValue: true,
          attributeFilter: [
            'class',
            'style',
            'hidden',
            'data-variant-wizard-hidden',
            'data-productwiz-hidden',
            'data-pw-hidden',
            'data-globo-hidden',
            'data-glo-hidden'
          ]
        });
      });
      root.__woodarqGloVariantGalleryObserver = observer;
    }

    [80, 420, 1200].forEach(delay => window.setTimeout(() => syncFromCurrentSelection('initial'), delay));
  };

  const initAll = scope => {
    const context = scope || document;
    if (context.matches?.(ROOT_SELECTOR)) initRoot(context);
    context.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', event => initAll(event.target));
})();
